import { Injectable } from "@angular/core";
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, toArray, mergeMap, take, tap } from 'rxjs/operators';
import { LocationFacebookToken, LocationFacebookTokenPair, FacebookToken, FacebookAuthResult, SocialSharingTemplateSettings } from '@models/index';
import { FacebookService, LoginResponse } from 'ngx-facebook';
import { SocialSharingScheduleSettings } from '@models/socialSharingScheduleSettings.model';
import { facebookScope } from "@utilities/function-utils";

@Injectable({
    providedIn: 'root'
})
export class FacebookServices {
    constructor(
        private http: HttpClient,
        private facebookService: FacebookService,
    ) {
        this.facebookService.init({
            version: 'v15.0',
            appId: environment.facebookAppId
        }).catch(err => { if (environment.showConsole) console.error('FB: error initializing:', err) });
    }

    /***************
     ** PAGE MGMT **
     ***************/

    findFacebookPages$(payload: { locationId: string, TypeService:string }): Observable<LocationFacebookTokenPair[]> {
        console.log('[facebook.service findFacebookPages$] start');
        const { locationId, TypeService } = payload;
        const path = `${environment.apiPath}/socialSharing/${locationId}/facebook/page?type=${TypeService}`;

        return this.http.get<LocationFacebookToken[]>(path, {
            headers: { accept: 'application/json' },
        }).pipe(
            tap(response => {
                console.log('[facebook.service findFacebookPages$] response:', response);
                console.log('[facebook.service findFacebookPages$] response:', JSON.stringify(response));
            }),
            (obs) => this.rx_getPairFromLocationFacebookToken(obs),
            catchError(error => throwError(() => ({
                message: `An unknown error has occurred.`,
                cause: error,
            }))),
        );
    }

    searchFacebookPages$(payload: { locationId: string, authResult: FacebookAuthResult }): Observable<LocationFacebookTokenPair[]> {
        console.log('[facebook.service searchFacebookPages$] start');
        const { locationId, authResult } = payload;
        const path = `${environment.apiPath}/socialSharing/${locationId}/facebook/pages/tokens`;

        return this.http.post<LocationFacebookToken[]>(path, authResult).pipe(
            tap(response => {
                console.log('[facebook.service searchFacebookPages$] response:', response);
                console.log('[facebook.service searchFacebookPages$] response:', JSON.stringify(response));
            }),
            switchMap(response => Array.isArray(response) ? of(response) : throwError(() => ({
                message: 'The server did not provide a list of associated tokens upon success.',
            }))),
            (obs) => this.rx_getPairFromLocationFacebookToken(obs),
            catchError(error => throwError(() => ({
                message: `An unknown error has occurred.`,
                cause: error,
            }))),
        );
    }

    private toggleFacebookPage$<T extends { locationId: string, locationTokenId: string }>(
        action: 'enable' | 'disable',
        payload: T
    ): Observable<LocationFacebookTokenPair> {
        console.log('[facebook.service toggleFacebookPage$] start');
        const { locationId, locationTokenId } = payload;
        const path = `${environment.apiPath}/socialSharing/${locationId}/facebook/${action}`;

        return this.http.get<LocationFacebookToken>(path, {
            headers: { accept: 'application/json' },
            params: { tokenId: locationTokenId },
        }).pipe(
            switchMap(response => response.id ? of(response) : throwError(() => ({
                message: 'The server did not provide a LocationFacebookToken as a response.',
            }))),
            switchMap(token => this.getPairFromLocationFacebookToken$(token)),
            catchError(error => throwError(() => ({
                message: `Unable to ${action} the Facebook page at this time. Please try again later.`,
                cause: error,
            }))),
        );
    }

    enableFacebookPage$<T extends { locationId: string, locationTokenId: string }>(
        payload: T
    ): Observable<LocationFacebookTokenPair> {
        console.log('[facebook.service enableFacebookPage$] start');
        return this.toggleFacebookPage$('enable', payload)
    }

    disableFacebookPage$<T extends { locationId: string, locationTokenId: string }>(
        payload: T
    ): Observable<LocationFacebookTokenPair> {
        console.log('[facebook.service disableFacebookPage$] start');
        return this.toggleFacebookPage$('disable', payload)
    }

    removeFacebookPage$<T extends { locationId: string, locationTokenId: string }>(
        payload: T
    ): Observable<boolean> {
        console.log('[facebook.service removeFacebookPage$] start');
        const { locationId, locationTokenId } = payload;
        const path = `${environment.apiPath}/socialSharing/${locationId}/facebook/page`;

        return this.http.delete<{ pages: LocationFacebookToken[], success: boolean }>(path, {
            headers: { accept: 'application/json' },
            params: { tokenId: locationTokenId }
        }).pipe(
            switchMap(response => 'success' in response ? of(response) : throwError(() => ({
                message: 'The server did not provide a success object as a response.',
            }))),
            switchMap(response => response.success ? of(response) : throwError(() => ({
                message: 'The server failed to delete the Facebook page for an unknown reason.',
            }))),
            catchError(error => throwError(() => ({
                message: `Unable to delete the Facebook page at this time. Please try again later.`,
                cause: error,
            }))),
            map(response => response.success),
        );
    }

    /****************
     ** SCHEDULING **
     ****************/

    getScheduleSettings$<T extends { locationId: string }>(
        payload: T
    ): Observable<SocialSharingScheduleSettings> {
        const { locationId } = payload;
        const path = `${environment.apiPath}/socialSharing/${locationId}/schedule`;

        return this.http.get<SocialSharingScheduleSettings>(path).pipe(
            switchMap((response) => response && response.locationId
                ? of(response)
                : throwError(() => ({
                    message: 'The server did not provide a settings object for the given location id.'
                }))
            ),
            catchError(error => throwError(() => ({
                message: `An unknown error has occurred.`,
                cause: error
            })))
        )
    }

    updateScheduleSettings$<T extends { settings: SocialSharingScheduleSettings }>(
        payload: T
    ): Observable<SocialSharingScheduleSettings> {
        const { settings } = payload;
        const { locationId } = settings;
        const path = `${environment.apiPath}/socialSharing/${locationId}/schedule`;

        return this.http.put<SocialSharingScheduleSettings>(path, settings).pipe(
            switchMap((response) => response && response.id ? of(response) : throwError(() => ({
                message: 'The server did not provide an ObjectId for the savd settings',
            }))),
            catchError(error => throwError(() => ({
                message: `An unknown error has occurred.`,
                cause: error
            }))),
        )
    }

    /****************
     ** TEMPLATING **
     ****************/

    getTemplateSettings$<T extends { locationId: string }>(
        payload: T
    ): Observable<SocialSharingTemplateSettings> {
        const { locationId } = payload;
        const path = `${environment.apiPath}/socialSharing/${locationId}/template`;

        return this.http.get<SocialSharingTemplateSettings>(path).pipe(
            switchMap((response) => response && response.locationId
                ? of(response)
                : throwError(() => ({
                    message: 'The server did not provide a template settings object for the given location id.'
                }))
            ),
            catchError((error) => throwError(() => ({
                message: `An unknown error has occurred.`,
                cause: error
            })))
        )
    }

    updateTemplateSettings$<T extends { settings: SocialSharingTemplateSettings }>(
        payload: T
    ): Observable<SocialSharingTemplateSettings> {
        const { settings } = payload;
        const { locationId } = settings;
        const path = `${environment.apiPath}/socialSharing/${locationId}/template`;

        if (environment.showConsole) {
            console.log('[social-sharing.service updateTemplateSettings$]', 'settings:', settings);
        }

        return this.http.put<SocialSharingTemplateSettings>(path, settings).pipe(
            switchMap((response) => response && response.id ? of(response) : throwError(() => ({
                message: 'The server did not provide an ObjectId for the saved template settings',
            }))),
            catchError((error) => throwError(() => ({
                message: `An unknown error has occurred.`,
                cause: error
            }))),
            tap((response) => {
                if (environment.showConsole) {
                    console.log('[social-sharing.service updateTemplateSettings$]', 'response:', response);
                }
            })
        )
    }

    /**********
     ** AUTH **
     **********/

    setupPagesFromAuthResultForLocation$(
        payload: { locationId: string, authResult: FacebookAuthResult }
    ): Observable<LocationFacebookTokenPair[]> {
        console.log('[facebook.service setupPagesFromAuthResultForLocation$] start');
        const { locationId, authResult } = payload;
        const path = `${environment.apiPath}/socialSharing/${locationId}/facebook/auth?type=${authResult.typeService}`;

        return this.http.post<LocationFacebookToken[]>(path, authResult).pipe(
            tap(response => {
                console.log('[facebook.service setupPagesFromAuthResultForLocation$] response', response);
                console.log('[facebook.service setupPagesFromAuthResultForLocation$] response', JSON.stringify(response));
            }),
            switchMap(response => Array.isArray(response) ? of(response) : throwError(() => ({
                message: 'The server did not provide a list of associated tokens upon success.',
            }))),
            (obs) => this.rx_getPairFromLocationFacebookToken(obs),
            catchError((error) => throwError(() => ({
                message: `An unknown error has occurred.`,
                cause: error,
            }))),
        );
    }

    setPageToken$(
        payload: { locationId: string, authResult: FacebookAuthResult, selectedPageId: any }
    ): Observable<LocationFacebookTokenPair[]> {
        console.log('[facebook.service setPageToken$] start');
        const { locationId, authResult, selectedPageId } = payload;
        const path = `${environment.apiPath}/socialSharing/${locationId}/facebook/auth/page/${selectedPageId}?type=${authResult.typeService}`;

        return this.http.post<LocationFacebookToken[]>(path, authResult).pipe(
            tap(response => {
                console.log('[facebook.service setPageToken$] response:', response);
                console.log('[facebook.service setPageToken$] response:', JSON.stringify(response));
            }),
            switchMap(response => Array.isArray(response) ? of(response) : throwError(() => ({
                message: 'The server did not provide a list of associated tokens upon success.',
            }))),
            (obs) => this.rx_getPairFromLocationFacebookToken(obs),
            catchError((error) => throwError(() => ({
                message: `An unknown error has occurred.`,
                cause: error,
            }))),
        );
    }

    authenticateWithFacebook(typeService: string, userId: string): Observable<FacebookAuthResult> {
        console.log('[facebook.service authenticateWithFacebook] start');

        return from(this.facebookService.login({
            scope: facebookScope(typeService),
            return_scopes: true,
            enable_profile_selector: true,
        })).pipe(
            switchMap((response: LoginResponse) => {
                // TODO: parse status and throwError if not acceptable
                console.log('[facebook.service authenticateWithFacebook] response:', response);
                console.log('[facebook.service authenticateWithFacebook] response:', JSON.stringify(response));

                const { authResponse } = response;

                // TODO: parse granted permissions and throwError if not acceptable
                console.log('[facebook.service authenticateWithFacebook] authResponse:', response.authResponse);
                console.log('[facebook.service authenticateWithFacebook] authResponse:', JSON.stringify(response.authResponse));
                console.log('[facebook.service authenticateWithFacebook] authResponse.grantedScopes:', response.authResponse.grantedScopes);
                console.log('[facebook.service authenticateWithFacebook] authResponse.grantedScopes:', JSON.stringify(response.authResponse.grantedScopes));

                return of({ ...authResponse, facebookAccountId: authResponse.userID, userId: userId, typeService: typeService});
            })
        );
    }

    shareReviewFromLocationToPage$(
        payload: { locationFacebookTokenPair: LocationFacebookTokenPair, reviewId: string }
    ) {
        const { locationFacebookTokenPair, reviewId } = payload;
        const { id, locationId } = locationFacebookTokenPair.locationToken;
        const { pageId } = locationFacebookTokenPair.token;
        const path = `${environment.apiPath}/socialSharing/${locationId}/facebook/share`;

        return this.http.post<{ postUrl?: string, success: boolean, error?: string }>(path, {}, {
            headers: { accept: 'application/json' },
            params: { pageId, reviewId, locationFacebookTokenId: id }
        }).pipe(
            tap(response => {
                console.log('[facebook.service shareReviewFromLocationToPage$] response', response);
            }),
            switchMap(response => ('success' in response) ? of(response) : throwError(() => ({
                message: 'The server did not provide a valid success or failure response for sharing the review.'
            }))),
            switchMap(response => response.success ? of(response) : throwError(() => ({
                message: 'The server failed to share the review to the provided Facebook page.',
                ...(response.error ? { cause: { message: response.error } } : {})
            }))),
            catchError((error) => throwError(() => ({
                message: `Unable to share the review to Facebook at this time. Please try again later.`,
                cause: error,
            }))),
        );
    }

    private rx_getPairFromLocationFacebookToken(
        obs: Observable<LocationFacebookToken[]>
    ): Observable<LocationFacebookTokenPair[]> {
        return obs.pipe(
            switchMap(tokens => from(tokens).pipe(
                mergeMap(token => {
                    return this.getPairFromLocationFacebookToken$(token);
                }),
            )),
            toArray()
        )
    }

    private getPairFromLocationFacebookToken$(locationToken: LocationFacebookToken): Observable<LocationFacebookTokenPair> {
        console.log('[facebook.service getPairFromLocationFacebookToken$] start');

        // TODO: locationId not needed once endpoint is moved
        const getTokenPath = ({ locationId, id }) =>
            `${environment.apiPath}/socialSharing/${locationId}/facebook/token/${id}`;

        const { tokenId, locationId } = locationToken;
        const path = getTokenPath({ locationId, id: tokenId });

        return this.http.get<FacebookToken>(path, {
            headers: { accept: 'application/json' }
        }).pipe(
            tap(response => {
                console.log('[facebook.service getPairFromLocationFacebookToken$] response:', response);
                console.log('[facebook.service getPairFromLocationFacebookToken$] response:', JSON.stringify(response));
            }),
            take(1),
            map(token => ({ token, locationToken: locationToken })),
            tap(data => console.log(data))
        )
    }
}
